import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EFonts } from 'libs/styles/styling';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

const MAP_STYLES = [
    'https://dvtqxks0zalev.cloudfront.net/app-static/style1.jpg',
    'https://dvtqxks0zalev.cloudfront.net/app-static/style2.jpg',
    'https://dvtqxks0zalev.cloudfront.net/app-static/demo2.jpg',
    'https://dvtqxks0zalev.cloudfront.net/app-static/style3.jpg',
    'https://dvtqxks0zalev.cloudfront.net/app-static/style4.jpg',
    'https://dvtqxks0zalev.cloudfront.net/app-static/style6.jpg'
];

const ICONS = ['car', 'home', 'star', 'heart', 'landmark', 'flag'];

const HeroSource = lazy(() => import('components/minute/layout/CreateMinuteBody/hero/hero'));
const PolygonSource = lazy(
    () => import('components/minute/layout/CreateMinuteBody/polygon/polygon')
);

export const CreateFeatures = (props: Props): JSX.Element => {
    const [index, setIndex] = useState(0);
    const [iconIndex, setIconIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((index) => (index + 1) % MAP_STYLES.length);
        }, 7000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setIconIndex((index) => (index + 1) % ICONS.length);
        }, 6000);

        return () => clearInterval(interval);
    }, []);

    function getNextIndex(index: number, move: number): number {
        return (index + move) % MAP_STYLES.length;
    }

    function getAnimation(degrees: number) {
        return [
            `rotate(${degrees}deg) scale(1.0)`,
            `rotate(${degrees}deg) scale(1.1)`,
            `rotate(${degrees}deg) scale(1.0)`
        ];
    }

    return (
        <S_CreateFeatures className={props.className}>
            <S_Item {...conditionalScaleAnimation(true, 1.02, 1.02)}>
                <S_Head>
                    <S_Image
                        style={{
                            left: 135,
                            top: -40
                        }}
                        animate={{
                            transform: getAnimation(15),
                            zIndex: [3, 99, 3]
                        }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 5, delay: 0 }}
                    >
                        <S_ImageContent $background={MAP_STYLES[getNextIndex(index, 2)]} />
                    </S_Image>
                    <S_Image
                        style={{
                            left: 65,
                            top: -60
                        }}
                        animate={{
                            transform: getAnimation(0),
                            zIndex: [2, 99, 2]
                        }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 5, delay: 1 }}
                    >
                        <S_ImageContent $background={MAP_STYLES[getNextIndex(index, 1)]} />
                    </S_Image>
                    <S_Image
                        style={{
                            left: -15,
                            top: -40
                        }}
                        animate={{
                            transform: getAnimation(-15),
                            zIndex: [1, 99, 1]
                        }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 5, delay: 2 }}
                        onAnimationComplete={() => {
                            setIndex((index) => (index + 1) % MAP_STYLES.length);
                        }}
                    >
                        <S_ImageContent $background={MAP_STYLES[index]} />
                    </S_Image>
                </S_Head>
                <S_Title>1. {Localization.HERO_BLOCK1_H1}</S_Title>
                <S_Desc>{Localization.HERO_BLOCK1_P1}</S_Desc>
            </S_Item>
            <S_Item {...conditionalScaleAnimation(true, 1.02, 1.02)}>
                <S_Head>
                    <S_CreateCar />
                    <S_Polygon>
                        <Suspense fallback={<></>}>
                            <PolygonSource />
                        </Suspense>
                    </S_Polygon>
                    <S_Pin
                        animate={{
                            rotate: [0, 360]
                        }}
                        transition={{ duration: 1, repeat: Infinity, repeatDelay: 5, delay: 0 }}
                    >
                        <S_Icon icon={['fas', ICONS[iconIndex] as IconName]} />
                    </S_Pin>
                </S_Head>
                <S_Title>2. {Localization.HERO_BLOCK2_H1}</S_Title>
                <S_Desc>{Localization.HERO_BLOCK2_P1}</S_Desc>
            </S_Item>
            <S_Item {...conditionalScaleAnimation(true, 1.02, 1.02)}>
                <S_Head>
                    <Suspense fallback={<></>}>
                        <HeroSource />
                    </Suspense>
                </S_Head>
                <S_Title>3. {Localization.HERO_BLOCK3_H1}</S_Title>
                <S_Desc>{Localization.HERO_BLOCK3_P1}</S_Desc>
            </S_Item>
        </S_CreateFeatures>
    );
};

const S_CreateFeatures = styled.div`
    display: flex;
`;

const S_Item = styled(motion.div)`
    border-radius: 7px;
    background: #efefef;
    padding: 20px 30px 40px 30px;
    display: flex;
    flex-direction: column;
    margin-top: 30px;
    flex-shrink: 0;
    width: 390px;
    &:not(:last-child) {
        margin: 30px 40px 0 0;
    }
`;

const S_Head = styled.div`
    position: relative;
    height: 250px;
`;

const S_Title = styled.div`
    color: #141c54;
    text-align: center;
    font-family: ${EFonts.Bold};
    font-size: 21px;
    font-style: normal;
    letter-spacing: -0.03em;
    margin: 0 0 15px 0;
`;

const S_Desc = styled.div`
    color: #141c54;
    text-align: center;
    font-family: ${EFonts.Regular};
    font-size: 15px;
    line-height: 25px;
`;

//1

const S_Image = styled(motion.div)`
    border-radius: 7px;
    border: 1px solid #dadada;
    background: linear-gradient(180deg, #eee 0%, #fff 100%);
    box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.05);
    width: 220px;
    height: 220px;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_ImageContent = styled.div<{ $background: string }>`
    width: 200px;
    height: 200px;
    background-image: url(${(props) => props.$background});
    border-radius: 7px;
    display: flex;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    border: 1px solid #dadada;
`;

const S_CreateCar = styled(motion.div)`
    width: 234px;
    height: 266px;
    background-image: url('https://dvtqxks0zalev.cloudfront.net/app-static/createcar.png');
    position: absolute;
    left: -20px;
    top: -40px;
    background-repeat: no-repeat;
    background-position: center;
`;

const S_Polygon = styled.div`
    position: absolute;
    right: -35px;
    top: 80px;
    width: 200px;
    height: 200px;
`;

const S_Pin = styled(motion.div)`
    position: absolute;
    right: 170px;
    top: 110px;
    border-radius: 50px;
    background: var(--acc-2-grad, linear-gradient(45deg, #bc904e 0%, #a33816 100%));
    width: 50px;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: #fff;
    font-size: 20px;
`;
